import { Breadcrumb, Button, Form, Input, Select, Table, Tag, Tooltip, Typography } from "antd";
import { ArrowLeftOutlined, HomeOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import { DatePicker as DatePickerJalali, JalaliLocaleListener } from "antd-jalali";
import { useEffect, useState } from "react";
import { _adr } from "../../address";
import { QueryAPI } from "../../tools/api";
import { toPascal } from "../../tools/impersonation";
import { NotFound } from "../../sources/svg/svg";
import "./loginAs.scss";

const PAGE_SIZE = 20;
const STATUS = {
	Active: { label: "فعال", color: "success" },
	Closed: { label: "خارج شده", color: "default" },
	Expired: { label: "منقضی شده", color: "warning" },
};

const dateFmt = new Intl.DateTimeFormat("fa-IR-u-ca-persian", { year: "numeric", month: "2-digit", day: "2-digit" });
const timeFmt = new Intl.DateTimeFormat("fa-IR", { hour: "2-digit", minute: "2-digit" });

const When = ({ iso }) =>
	iso ? (
		<div className="logs-when">
			<span>{dateFmt.format(new Date(iso))}</span>
			<small>{timeFmt.format(new Date(iso))}</small>
		</div>
	) : (
		<span className="logs-muted">—</span>
	);

// session length; open sessions count up to now (active) or to their expiry
const duration = (row) => {
	const end = row.LogoutAt ?? (row.Status === "Active" ? new Date().toISOString() : row.ExpirationAt);
	const min = Math.max(0, Math.round((Date.parse(end) - Date.parse(row.LoginAt)) / 60000));
	if (Number.isNaN(min)) return "—";
	return min < 60 ? `${min} دقیقه` : `${Math.floor(min / 60)} ساعت${min % 60 ? ` و ${min % 60} دقیقه` : ""}`;
};

const columns = [
	{
		title: "کاربر اصلی ← نماینده",
		key: "who",
		render: (_, r) => (
			<div className="logs-who">
				<span>{r.ImpersonatorUsername}</span>
				<ArrowLeftOutlined aria-label="به" />
				<strong>{r.TargetUsername}</strong>
			</div>
		),
	},
	{ title: "ورود", dataIndex: "LoginAt", key: "LoginAt", render: (v) => <When iso={v} /> },
	{ title: "خروج", dataIndex: "LogoutAt", key: "LogoutAt", render: (v) => <When iso={v} /> },
	{ title: "مدت", key: "duration", render: (_, r) => duration(r) },
	{
		title: "وضعیت",
		dataIndex: "Status",
		key: "Status",
		render: (s) => (
			<Tag color={STATUS[s]?.color} className="logs-status">
				{STATUS[s]?.label ?? s}
			</Tag>
		),
	},
	{
		title: "IP",
		dataIndex: "Ip",
		key: "Ip",
		render: (ip, r) => (
			<Tooltip title={r.UserAgent}>
				<span className="logs-mono" dir="ltr">
					{ip}
				</span>
			</Tooltip>
		),
	},
	{
		title: "Session",
		dataIndex: "SessionId",
		key: "SessionId",
		render: (id) => (
			<Typography.Text className="logs-mono" copyable={{ text: id, tooltips: ["کپی", "کپی شد"] }} dir="ltr">
				{id?.slice(0, 10)}…
			</Typography.Text>
		),
	},
];

const LoginAsLogs = () => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState(null);
	const [page, setPage] = useState(1);

	const load = async (nextPage = 1) => {
		const v = form.getFieldsValue();
		const params = new URLSearchParams({ page: nextPage, pageSize: PAGE_SIZE });
		if (v.impersonatorUsername?.trim()) params.set("impersonatorUsername", v.impersonatorUsername.trim());
		if (v.dealerUsername?.trim()) params.set("dealerUsername", v.dealerUsername.trim());
		if (v.status) params.set("status", v.status);
		if (v.range?.[0]) params.set("from", v.range[0].startOf("day").toISOString());
		if (v.range?.[1]) params.set("to", v.range[1].endOf("day").toISOString());

		setLoading(true);
		// QueryAPI GET ignores `parameters`, so the filters travel in the query string
		const res = await QueryAPI({ url: `/dealer/Impersonation/Logs?${params}`, type: "get" });
		setResult(res?.Result ? toPascal(res.Result) : { Items: [], TotalCount: 0 });
		setPage(nextPage);
		setLoading(false);
	};

	// open with the latest sessions instead of an empty page
	useEffect(() => {
		load(1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const reset = () => {
		form.resetFields();
		load(1);
	};

	return (
		<div className="container-main container-login-as">
			<div className="g-hold-title-of-page">
				<Breadcrumb className="breadcrumbs" items={[{ href: _adr.Root + _adr.Home, title: <HomeOutlined /> }, { title: "گزارش ورود به عنوان" }]} />
				<div className="title-page">
					<h3>گزارش ورود به عنوان</h3>
					<p>هر بار که کاربری از طریق «ورود به عنوان» وارد پنل یک نماینده شده، اینجا ثبت می‌شود.</p>
				</div>
			</div>
			<div className="g-layout-content">
				<Content className="g-layout-main logs-page">
					<Form form={form} layout="vertical" onFinish={() => load(1)} className="logs-filters">
						<Form.Item name="impersonatorUsername" label="کاربر اصلی">
							<Input dir="ltr" allowClear placeholder="sales_user" />
						</Form.Item>
						<Form.Item name="dealerUsername" label="نام کاربری نماینده">
							<Input dir="ltr" allowClear placeholder="dealer001" />
						</Form.Item>
						<Form.Item name="status" label="وضعیت">
							<Select allowClear placeholder="همه" options={Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label }))} />
						</Form.Item>
						<JalaliLocaleListener />
						<Form.Item name="range" label="بازه تاریخ" className="logs-range">
							<DatePickerJalali.RangePicker dropdownAlign={{ overflow: { adjustY: 0 } }} format={"YYYY/MM/DD"} />
						</Form.Item>
						<div className="logs-actions">
							<Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
								جستجو
							</Button>
							<Button icon={<ReloadOutlined />} onClick={reset} aria-label="پاک کردن فیلترها" />
						</div>
					</Form>

					<section className="logs-table">
						<header>
							<h4>جلسه‌ها</h4>
							{result && <span className="logs-muted">{result.TotalCount ?? 0} مورد</span>}
						</header>
						<Table
							className="tbl-normal"
							rowKey="Id"
							columns={columns}
							dataSource={result?.Items ?? []}
							loading={loading}
							scroll={{ x: 900 }}
							rowClassName={(r) => (r.Status === "Active" ? "is-active-row" : "")}
							locale={{ emptyText: <NotFound text={"جلسه‌ای با این فیلترها پیدا نشد."} /> }}
							pagination={{
								current: page,
								pageSize: PAGE_SIZE,
								total: result?.TotalCount ?? 0,
								showSizeChanger: false,
								hideOnSinglePage: true,
								onChange: load,
							}}
						/>
					</section>
				</Content>
			</div>
		</div>
	);
};

export default LoginAsLogs;
